import { useState, type FormEvent } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { createFeedback } from '@/features/feedback/api'
import { collectAttachedState } from '@/features/feedback/attachedStateCollector'

/** Which full-screen gate the clinician is stuck behind. */
export type GateKind = 'agreement' | 'email_unverified'

/** Generous for a free-text report; stops a pasted novel reaching triage. */
export const GATE_HELP_MAX_LENGTH = 4000

/**
 * "Stuck here? Tell us" on the full-screen account gates (CLA-1390).
 *
 * The agreement gate and the lapsed email-verification gate both take the
 * whole screen, and the in-app feedback widget lives inside the shell they
 * replace — so until this existed a clinician stuck behind either had no way
 * to tell anyone. The backend exempts exactly `POST /api/v1/feedback` from
 * both gates for this, so a report lands in the ordinary feedback queue
 * (Slack card, triage, reply) as the signed-in user, like any other.
 *
 * Self-contained on purpose: no toast (the ToastContainer is inside the
 * gated shell too) and no "Your reports" view (that list is still gated).
 * The confirmation renders in place.
 */
export function GateHelp({ gate, placeholder }: { gate: GateKind; placeholder?: string }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [reference, setReference] = useState<string | null>(null)

  const trimmed = message.trim()
  const canSend = trimmed.length > 0 && state !== 'sending'

  async function send(e: FormEvent) {
    e.preventDefault()
    if (!canSend) return
    setState('sending')
    try {
      const attached = collectAttachedState()
      const resp = await createFeedback({
        message: trimmed.slice(0, GATE_HELP_MAX_LENGTH),
        platform: 'web',
        entry_point: 'contextual_prompt',
        source_url: attached.route,
        attached_state: { ...attached, kind: 'blocked_gate', gate },
        app_version: import.meta.env.VITE_RELEASE,
      })
      setReference(resp.reference)
      setState('sent')
    } catch {
      setState('error')
    }
  }

  if (state === 'sent') {
    return (
      <p role="status" className="inline-flex items-start gap-1.5 text-sm text-ink-secondary">
        <Check className="w-4 h-4 mt-0.5 text-claro-600 shrink-0" aria-hidden />
        <span>
          Sent{reference ? ` (${reference})` : ''}. The Claro team will get back to you.
        </span>
      </p>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm text-ink-secondary hover:text-ink underline underline-offset-2"
      >
        Stuck here? Tell us what's wrong
      </button>
    )
  }

  return (
    <form onSubmit={send} className="w-full space-y-2">
      <label htmlFor={`gate-help-${gate}`} className="block text-sm font-medium text-ink">
        What's going wrong?
      </label>
      <textarea
        id={`gate-help-${gate}`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={GATE_HELP_MAX_LENGTH}
        rows={3}
        autoFocus
        placeholder={placeholder}
        className="w-full rounded-lg border border-border-input bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-claro-600"
      />
      {state === 'error' && (
        <p className="text-xs text-on-error-soft">Couldn't send that — please try again.</p>
      )}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!canSend}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-claro-600 rounded-lg hover:bg-claro-700 disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed transition-colors"
        >
          {state === 'sending' && <Loader2 className="w-4 h-4 animate-spin" />}
          Send to the Claro team
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm text-ink-secondary hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
